/**
 * Application header with navigation
 * Uses Meridian Masthead with inline MastheadLink navigation
 */

import React from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import Masthead, { MastheadTitle, MastheadLink } from '@amzn/meridian/masthead';
import { useNewSession } from '../../../hooks/useNewSession';
import { useHardReloadIfPolling } from '../../../hooks/useHardReloadIfPolling';
import { useSimulationDraft } from '../../../hooks/useSimulationDraft';
import { useAppSelector } from '../../../store/store';
import { hasLength } from '../../../utils/dataTransformations';
import { withCondition } from '../../../utils/withCondition';
import SimulationControlComponent from '../simulation/SimulationControl';
import navigatorLogo from '../../../images/header_logo.png';

const LinkWithCondition = withCondition(MastheadLink);
const SimulationControlWithCondition = withCondition(SimulationControlComponent);

const Header: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const startNewSession = useNewSession();
  const hardReloadIfPolling = useHardReloadIfPolling();
  const isAdmin = useAppSelector((state) => state.user.isAdmin);
  const simulatableLdap = useAppSelector((state) => state.user.simulatableLdap);
  const simulatedLdap = useAppSelector((state) => state.user.simulatedLdap);
  const { selected, selectGroups, switchSimulation } = useSimulationDraft(simulatedLdap);
  const AdminLink = LinkWithCondition(isAdmin);
  const SimulationControl = SimulationControlWithCondition(hasLength(simulatableLdap));

  const navigateTo = (path: string) => {
    if (!hardReloadIfPolling(path)) navigate(path);
  };

  const { pathname } = location;
  // Which paths ARE a session, not which ones are not: as a list of exclusions this went
  // stale the moment a route was added, leaving New Session underlined alongside the real
  // tab. '/' is included because it redirects to /session/new. '/sessions' does not match,
  // since the trailing slash in '/session/' stops it.
  const isNewSession = pathname === '/' || pathname.startsWith('/session/');

  return (
    <div className="app-masthead">
      <Masthead backgroundColor="#262637" navigationLabel="Main navigation">
        <MastheadTitle>
          <img
            src={navigatorLogo}
            alt="WHS Navigator"
            style={{ height: '36px', cursor: 'pointer' }}
            onClick={() => startNewSession()}
          />
        </MastheadTitle>
        <MastheadLink selected={isNewSession} onClick={() => startNewSession()}>
          New Session
        </MastheadLink>
        <MastheadLink selected={pathname === '/sessions'} onClick={() => navigateTo('/sessions')}>
          Session History
        </MastheadLink>
        <AdminLink selected={pathname === '/profiles'} onClick={() => navigateTo('/profiles')}>
          Profiles
        </AdminLink>
        <AdminLink selected={pathname === '/site-members'} onClick={() => navigateTo('/site-members')}>
          Site Management
        </AdminLink>
        <AdminLink selected={pathname === '/metrics'} onClick={() => navigateTo('/metrics')}>
          Metrics
        </AdminLink>
        <MastheadLink selected={pathname === '/resources'} onClick={() => navigateTo('/resources')}>
          Resources
        </MastheadLink>
        <SimulationControl
          groups={simulatableLdap}
          selected={selected}
          onSelect={selectGroups}
          checked={hasLength(simulatedLdap)}
          onChange={switchSimulation}
        />
      </Masthead>
    </div>
  );
};

export default Header;
